/**
 * Sound effects for game events.
 * Uses the technology pack only (public/assets/sounds/technology/).
 * Volume is default 0.2 and user-adjustable via drawer or settings.
 * See public/assets/sounds/README.md.
 *
 * When host is playing on this device (same room), sounds are muted for that session
 * so the phone does not play music/effects in the same room as others.
 */

const SOUNDS_BASE = '/assets/sounds';

/** When true, all playSound calls are no-ops (e.g. host playing on this device in same room). */
let mutedForSession = false;

export function setSoundsMutedForSession(muted: boolean): void {
  mutedForSession = muted;
}

export function getSoundsMutedForSession(): boolean {
  return mutedForSession;
}

/** Pack used for all SFX (fixed; no pack selector in UI). */
const SFX_PACK = 'technology';

export type SoundId =
  | 'room-created'
  | 'room-joined'
  | 'player-joined'
  | 'game-start'
  | 'game-finished'
  | 'return-to-waiting';

const SOUND_FILES: Record<SoundId, string> = {
  'room-created': 'room-created.mp3',
  'room-joined': 'room-joined.mp3',
  'player-joined': 'player-joined.mp3',
  'game-start': 'game-start.mp3',
  'game-finished': 'game-finished.mp3',
  'return-to-waiting': 'return-to-waiting.mp3',
};

const STORAGE_KEY_ENABLED = 'toyo-sounds-enabled';
const STORAGE_KEY_VOLUME = 'toyo-sounds-volume';
const DEFAULT_ENABLED = true;
const DEFAULT_VOLUME = 0.2;

function isEnabled(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ENABLED);
    if (stored === null) return DEFAULT_ENABLED;
    return stored === 'true';
  } catch {
    return DEFAULT_ENABLED;
  }
}

function setEnabled(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY_ENABLED, String(value));
  } catch {
    // ignore
  }
}

function getVolumeStored(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_VOLUME);
    if (stored === null) return DEFAULT_VOLUME;
    const n = parseFloat(stored);
    if (Number.isNaN(n) || n < 0 || n > 1) return DEFAULT_VOLUME;
    return n;
  } catch {
    return DEFAULT_VOLUME;
  }
}

function setVolumeStored(value: number): void {
  try {
    const v = Math.max(0, Math.min(1, value));
    localStorage.setItem(STORAGE_KEY_VOLUME, String(v));
  } catch {
    // ignore
  }
}

/**
 * Play a sound by id. No-op if sounds are disabled or file fails to load.
 * Uses technology pack and stored volume (default 0.2).
 */
export function playSound(id: SoundId): void {
  if (mutedForSession || !isEnabled()) return;
  const filename = SOUND_FILES[id];
  if (!filename) return;
  const src = `${SOUNDS_BASE}/${SFX_PACK}/${filename}`;
  const audio = new Audio(src);
  audio.volume = getVolumeStored();
  audio.play().catch(() => {
    // User may not have interacted yet, or file missing — fail silently
  });
}

export function setSoundsEnabled(enabled: boolean): void {
  setEnabled(enabled);
}

export function getSoundsEnabled(): boolean {
  return isEnabled();
}

export function getVolume(): number {
  return getVolumeStored();
}

export function setVolume(value: number): void {
  setVolumeStored(value);
}

/** Convenience: play sound when host creates a room */
export function playRoomCreated(): void {
  playSound('room-created');
}

/** Convenience: play when a player successfully joins a room */
export function playRoomJoined(): void {
  playSound('room-joined');
}

/** Convenience: play when another player joins the room (host + players hear) */
export function playPlayerJoined(): void {
  playSound('player-joined');
}

/** Convenience: play when the game starts */
export function playGameStart(): void {
  playSound('game-start');
}

/** Convenience: play when the game ends (leaderboard / game over screen) */
export function playGameFinished(): void {
  playSound('game-finished');
}

/** Convenience: play when returning to waiting room after a game */
export function playReturnToWaiting(): void {
  playSound('return-to-waiting');
}

/** Play bonus level intro sound (assets/sounds/bonusLevel/intro/). No-op if muted or disabled. */
export function playBonusIntroSound(): void {
  if (mutedForSession || !isEnabled()) return;
  const src = `${SOUNDS_BASE}/bonusLevel/intro/mixkit-game-bonus-reached-2065.wav`;
  const audio = new Audio(src);
  audio.volume = getVolumeStored();
  audio.play().catch(() => {});
}

/** Play bonus level countdown tick (3-2-1) sound (assets/sounds/bonusLevel/countdown/). No-op if muted or disabled. */
export function playBonusCountdownSound(): void {
  if (mutedForSession || !isEnabled()) return;
  const src = `${SOUNDS_BASE}/bonusLevel/countdown/mixkit-simple-countdown-922.wav`;
  const audio = new Audio(src);
  audio.volume = getVolumeStored();
  audio.play().catch(() => {});
}

/** Last typewriter Audio played (so we can stop it when round ends). */
let lastTypewriterAudio: HTMLAudioElement | null = null;

/** Play typewriter/typing sound (assets/sounds/bonusLevel/typewriter/). Returns the Audio so caller can stop it; no-op if muted or disabled. */
export function playTypewriterSound(): HTMLAudioElement | null {
  if (mutedForSession || !isEnabled()) return null;
  const src = `${SOUNDS_BASE}/bonusLevel/typewriter/mixkit-keyboard-typing-1386.wav`;
  const audio = new Audio(src);
  lastTypewriterAudio = audio;
  audio.volume = getVolumeStored();
  audio.play().catch(() => {});
  return audio;
}

/** Stop the typewriter sound (e.g. when everyone has submitted). */
export function stopTypewriterSound(): void {
  if (lastTypewriterAudio) {
    lastTypewriterAudio.pause();
    lastTypewriterAudio.currentTime = 0;
    lastTypewriterAudio = null;
  }
}

/** Play bonus round winner sound (assets/sounds/bonusLevel/winner/). No-op if muted or disabled. */
export function playBonusWinnerSound(): void {
  if (mutedForSession || !isEnabled()) return;
  const src = `${SOUNDS_BASE}/bonusLevel/winner/mixkit-fantasy-game-success-notification-270.wav`;
  const audio = new Audio(src);
  audio.volume = getVolumeStored();
  audio.play().catch(() => {});
}

/** Play one of the WhosOnImage glitch sounds once when the image animation starts (no loop, no setInterval). */
export function playWhosOnImageGlitchSound(): void {
  if (mutedForSession || !isEnabled()) return;
  const src = `${SOUNDS_BASE}/bonusLevel/glitch/mixkit-static-electric-crackles-1455.wav`;
  const audio = new Audio(src);
  audio.volume = getVolumeStored();
  audio.play().catch(() => {});
}
